import { useEffect, useRef, useState } from "react";
import type { DocumentBackground, TemplateElement, WildcardKey } from "./Documents.types";
import { backgroundLayerStyle, elementPositionStyle, isTextLike, resolveText, textElementStyle } from "./documentRender.utils";

type Props = {
  background: DocumentBackground;
  elements: TemplateElement[];
  wildcardValues?: Partial<Record<WildcardKey, string>>;
  maxWidth?: number; // optional cap; otherwise fills all available parent width
  containerId?: string;
};

// Read-only certificate renderer used by TemplateEditor's "Preview" dialog
// (real self-serve download lives in the Flutter app, not this dashboard).
// Measures its own parent via ResizeObserver so it always uses the maximum
// width the surrounding layout gives it (capped by `maxWidth`, if provided).
export default function DocumentCanvasView({ background, elements, wildcardValues, maxWidth, containerId }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [ availableWidth, setAvailableWidth ] = useState(maxWidth ?? background.width);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setAvailableWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const effectiveWidth = maxWidth ? Math.min(maxWidth, availableWidth) : availableWidth;
  const scale = Math.max(0.05, Math.min(1, effectiveWidth / background.width));

  return (
    <div ref={wrapRef} style={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <div
        id={containerId}
        style={{
          position: "relative",
          width: background.width * scale,
          height: background.height * scale,
          background: "#ffffff",
          boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        {background.url && <div style={backgroundLayerStyle(background)} />}
        {elements
          .slice()
          .sort((a, b) => a.zIndex - b.zIndex)
          .map((el) => (
            <div key={el.id} style={elementPositionStyle(el, scale)}>
              {isTextLike(el) ? (
                <div style={textElementStyle(el, scale)}>{resolveText(el, wildcardValues)}</div>
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}>
                  <div style={{ width: "100%", borderBottom: `2px solid ${el.color}` }} />
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
